import { Operation, compare } from 'fast-json-patch';
import { omit, sortBy } from 'lodash';
import { logger } from '../../../../util/logger';
import { ScratchpadAppDataDiffFn } from '../../../app-slugs';
import { Flow, FlowItem, FlowItemParameter } from '../model/flow.types';
import { isFlowAppData } from './flow.util';

export type FlowDiffableObject<FlowConfig = Record<string, unknown>> = Omit<
  Flow<FlowConfig>,
  'items' | 'defaultParameterSet' | 'weldParameterSet'
> & {
  items: FlowItemDiffableObject;
  defaultParameterSet?: {
    data: {
      parameters: FlowItemParameterDiffableObject;
    };
  };
  weldParameterSet?: {
    data: {
      parameters: FlowItemParameterDiffableObject;
    };
  };
};

export type FlowItemDiffableObject = Record<
  string,
  Omit<FlowItem, 'parameters'> & {
    parameters: FlowItemParameterDiffableObject;
    __index__: number;
  }
>;

export type FlowItemParameterDiffableObject = Record<string, FlowItemParameter>;

/**
 * Calculates the differences between two scratchpad app datas of type Flow.
 */
const diffFlowAppData: ScratchpadAppDataDiffFn = ({
  oldAppData,
  newAppData,
}) => {
  const oldFlow = { ...oldAppData } as Flow;
  const newFlow = { ...newAppData } as Flow;

  // it is possible that the flow items are not defined for new parts
  oldFlow.items ||= [];
  oldFlow.config ||= {};

  newFlow.items ||= [];
  newFlow.config ||= {};

  if (!isFlowAppData(oldFlow)) {
    logger.warn('comparing app data that is missing flow properties', {
      oldFlow,
    });
  }

  if (!isFlowAppData(newFlow)) {
    logger.warn('comparing app data that is missing flow properties', {
      newFlow,
    });
  }

  const oldFlowDiffableObject = convertFlowToDiffableObject({
    flow: oldFlow,
  });

  const newFlowDiffableObject = convertFlowToDiffableObject({
    flow: newFlow,
  });

  const operations = computeFlowPatchOperations({
    oldFlow: oldFlowDiffableObject,
    newFlow: newFlowDiffableObject,
  });

  return operations;
};

const convertFlowToDiffableObject = <
  FlowConfig = Record<string, unknown>,
>(options: {
  flow: Flow<FlowConfig>;
}): FlowDiffableObject<FlowConfig> => {
  const { flow } = options;

  const { defaultParameterSet, weldParameterSet, items, ...rest } = flow;

  const object: FlowDiffableObject<FlowConfig> = {
    ...rest,
    items: convertFlowItemsToDiffableObject({
      flowItems: items,
    }),
  };

  if (defaultParameterSet?.data.parameters) {
    object.defaultParameterSet = {
      ...flow.defaultParameterSet,
      data: {
        parameters: convertFlowItemParametersToDiffableObject({
          flowItemParams: defaultParameterSet.data.parameters,
        }),
      },
    };
  }

  if (weldParameterSet?.data.parameters) {
    object.weldParameterSet = {
      ...flow.weldParameterSet,
      data: {
        parameters: convertFlowItemParametersToDiffableObject({
          flowItemParams: weldParameterSet.data.parameters,
        }),
      },
    };
  }

  return object;
};

const convertFlowItemsToDiffableObject = (options: {
  flowItems: Array<FlowItem>;
}): FlowItemDiffableObject => {
  const { flowItems } = options;

  const object = flowItems.reduce((flowItemsObj, flowItem, index) => {
    if (flowItem.id) {
      flowItemsObj[flowItem.id] = {
        ...flowItem,
        parameters: convertFlowItemParametersToDiffableObject({
          flowItemParams: flowItem.parameters,
        }),
        __index__: index,
      };
    }
    return flowItemsObj;
  }, {} as FlowItemDiffableObject);

  return object;
};

const convertFlowItemParametersToDiffableObject = (options: {
  flowItemParams: Array<FlowItemParameter>;
}): FlowItemParameterDiffableObject => {
  const { flowItemParams } = options;

  const object = flowItemParams.reduce((flowItemParamsObj, flowItemParam) => {
    if (flowItemParam.ref) {
      flowItemParamsObj[flowItemParam.ref] = {
        ...flowItemParam,
      };
    }
    return flowItemParamsObj;
  }, {} as FlowItemParameterDiffableObject);

  return object;
};

const convertDiffableObjectToFlow = <
  FlowConfig = Record<string, unknown>,
>(options: {
  diffableFlow: FlowDiffableObject<FlowConfig>;
}): Flow<FlowConfig> => {
  const { diffableFlow } = options;

  const { items, defaultParameterSet, weldParameterSet, ...rest } =
    diffableFlow;

  const flow: Flow<FlowConfig> = {
    ...rest,
    items: convertDiffableObjectToFlowItems({
      diffableItems: items,
    }),
  };

  if (defaultParameterSet?.data.parameters) {
    flow.defaultParameterSet = {
      ...diffableFlow.defaultParameterSet,
      data: {
        parameters: convertDiffableObjectToFlowItemParameters({
          diffableParams: defaultParameterSet.data.parameters,
        }),
      },
    };
  }

  if (weldParameterSet?.data.parameters) {
    flow.weldParameterSet = {
      ...diffableFlow.weldParameterSet,
      data: {
        parameters: convertDiffableObjectToFlowItemParameters({
          diffableParams: weldParameterSet.data.parameters,
        }),
      },
    };
  }

  return flow;
};

const convertDiffableObjectToFlowItems = (options: {
  diffableItems: FlowItemDiffableObject;
}): Array<FlowItem> => {
  const { diffableItems } = options;

  const items = Object.values(diffableItems).map((diffableItem) => {
    const flowItem = {
      ...diffableItem,
      parameters: convertDiffableObjectToFlowItemParameters({
        diffableParams: diffableItem.parameters,
      }),
    };

    return flowItem;
  });

  // Sort the elements by their new index.
  const sortedItems = sortBy(items, ['__index__']);

  // Remove the metadata index property, just for diffing purposes.
  const mappedItems = sortedItems.map((item): FlowItem => {
    return omit(item, '__index__');
  });

  return mappedItems;
};

const convertDiffableObjectToFlowItemParameters = (options: {
  diffableParams: FlowItemParameterDiffableObject;
}): Array<FlowItemParameter> => {
  const { diffableParams } = options;

  return Object.values(diffableParams);
};

const computeFlowPatchOperations = (options: {
  oldFlow: FlowDiffableObject;
  newFlow: FlowDiffableObject;
}): Array<Operation> => {
  const { oldFlow, newFlow } = options;

  const patchOperations = compare(oldFlow, newFlow);

  return patchOperations;
};

export {
  diffFlowAppData,
  convertFlowToDiffableObject,
  convertFlowItemsToDiffableObject,
  convertFlowItemParametersToDiffableObject,
  computeFlowPatchOperations,
  convertDiffableObjectToFlow,
  convertDiffableObjectToFlowItems,
  convertDiffableObjectToFlowItemParameters,
};
